import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  formatRelativeTime, 
  calculateLaneProgress,
  cn 
} from '../lib/utils';
import { Layout } from '../components/Layout';
import { ProgressBar, MiniProgressBar } from '../components/ProgressBar';
import { StatusBadge } from '../components/StatusBadge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  Video, 
  Trophy, 
  ImageIcon, 
  ShoppingBag,
  ExternalLink,
  Clock,
  RefreshCw,
  Play,
  Download,
  Eye,
  UserCheck2
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';

// Helper Components - defined outside of render functions to prevent re-creation
function VideoCard({ url, label }) {
  return (
    <div className="bg-slate-100 rounded-lg overflow-hidden aspect-video relative group">
      {url ? (
        <>
          <video
            src={url}
            className="w-full h-full object-cover"
            preload="metadata"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-white rounded-full hover:bg-cyan-50 transition-colors"
            >
              <Play className="w-5 h-5 text-cyan-600" />
            </a>
            <a
              href={url}
              download
              className="p-3 bg-white rounded-full hover:bg-cyan-50 transition-colors"
            >
              <Download className="w-5 h-5 text-cyan-600" />
            </a>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <Video className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">{label}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function AdSetCard({ adNumber, images }) {
  const imageArray = Array.isArray(images) ? images : [];
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h4 className="text-sm font-semibold text-slate-600 mb-3">Ad Set {adNumber}</h4>
      <div className="grid grid-cols-2 gap-2">
        {imageArray.length > 0 ? (
          imageArray.slice(0, 4).map((url, i) => (
            <div key={i} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
              <img src={url} alt={`Ad ${adNumber}-${i + 1}`} className="w-full h-full object-cover" />
            </div>
          ))
        ) : (
          [...Array(4)].map((_, i) => (
            <div key={i} className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-slate-400" />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, content, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <h4 className="text-sm font-semibold text-slate-600">{title}</h4>
      </div>
      {content ? (
        <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-3 rounded-lg overflow-auto max-h-32 text-slate-700">
          {content}
        </pre>
      ) : (
        <p className="text-sm text-slate-400">Not generated</p>
      )}
    </div>
  );
}

const tabs = [
  { id: 'research', label: 'Research', icon: Search, lane: 'research' },
  { id: 'ugc', label: 'UGC Videos', icon: Video, lane: 'ugc' },
  { id: 'winning', label: 'Winning Product', icon: Trophy, lane: 'winning' },
  { id: 'ads', label: 'Static Ads', icon: ImageIcon, lane: 'ads' },
  { id: 'shopify', label: 'Shopify', icon: ShoppingBag, lane: 'shopify' },
];

export default function ProductDetail() {
  const { ecomId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('research');
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [relatedData, setRelatedData] = useState({});

  const fetchProductData = useCallback(async () => {
    try {
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('ecom_id', ecomId)
        .single();

      if (productError) throw productError;
      setProduct(productData);

      // Fetch all related data
      const [reviews, marketing, ugc, ugcFullVideos, ugcShorts, winning, winningImages, staticAds, shopify, shopifyImages, shopifySections] = await Promise.all([
        supabase.from('product_reviews').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('product_marketing_angles').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_ads').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_full_videos').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_shorts').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_details').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_images').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('static_ads').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('shopify').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('shopify_product_images').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('shopify_sections').select('*').eq('ecom_id', ecomId).single()
      ]);

      setRelatedData({
        reviews: reviews.data,
        marketing: marketing.data,
        ugc: ugc.data,
        ugcFullVideos: ugcFullVideos.data,
        ugcShorts: ugcShorts.data,
        winning: winning.data,
        winningImages: winningImages.data,
        staticAds: staticAds.data,
        shopify: shopify.data,
        shopifyImages: shopifyImages.data,
        shopifySections: shopifySections.data
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product details');
    } finally {
      setLoading(false);
    }
  }, [ecomId]);

  useEffect(() => {
    fetchProductData();

    // Real-time subscription
    const channel = supabase
      .channel(`product-${ecomId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products', filter: `ecom_id=eq.${ecomId}` }, fetchProductData)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'product_reviews', filter: `ecom_id=eq.${ecomId}` }, fetchProductData)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ugc_ads', filter: `ecom_id=eq.${ecomId}` }, fetchProductData)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [ecomId, fetchProductData]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-slate-600 mb-4">Product not found</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  const laneProgress = {
    research: calculateLaneProgress(product, relatedData, 'research'),
    ugc: calculateLaneProgress(product, relatedData, 'ugc'),
    winning: calculateLaneProgress(product, relatedData, 'winning'),
    ads: calculateLaneProgress(product, relatedData, 'ads'),
    shopify: calculateLaneProgress(product, relatedData, 'shopify')
  };

  const overallProgress = Math.round(
    (laneProgress.research + laneProgress.ugc + laneProgress.winning + laneProgress.ads + laneProgress.shopify) / 5
  );

  // Get first image from image_urls array
  const images = Array.isArray(product.image_urls) ? product.image_urls : [];
  const imageUrl = images.length > 0 ? images[0] : null;
  const requiresApproval =
    relatedData.winning?.status === 'Pending User Approval';

  return (
    <Layout productName={product.ecom_id}>
      <div data-testid="product-detail">
        {/* Header Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          {/* Top Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors group"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-cyan-600" />
              </button>
              
              <div className="flex items-center gap-4">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.ecom_id}
                    className="w-14 h-14 object-cover rounded-lg border-2 border-slate-200 shadow-sm"
                  />
                ) : (
                  <div className="w-14 h-14 bg-slate-100 rounded-lg border-2 border-slate-200 flex items-center justify-center">
                    <ExternalLink className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                
                <div>
                  <h1 className="text-xl font-bold text-[#1E1B4B] font-['Manrope']">
                    {product.ecom_id}
                  </h1>
                  <div className="flex items-center gap-3 text-sm text-slate-600 mt-0.5">
                    <span className="capitalize">{product.platform}</span>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(product.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <a
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'px-6 py-3 bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700',
                'text-white font-semibold rounded-lg transition-all',
                'shadow-md hover:shadow-lg flex items-center gap-2'
              )}
              data-testid="view-original-btn"
            >
              <ExternalLink className="w-4 h-4" />
              View Original
            </a>
          </div>

          {/* Progress Bar Row */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
              Overall Progress
            </span>
            <ProgressBar
              value={overallProgress}
              lane="default"
              size="lg"
              animated={overallProgress > 0 && overallProgress < 100}
            />
            <span className="text-base font-bold text-[#1E1B4B] whitespace-nowrap min-w-[3rem] text-right">
              {overallProgress}%
            </span>
          </div>
        </div>

        {/* Human-in-the-loop banner */}
        {requiresApproval && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <UserCheck2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-amber-800">
                  Human review required: confirm winning product details
                </h2>
                <p className="text-xs text-amber-700 mt-1">
                  Please review the generated name, description, features, and how-to-use for this winning product before it is used in downstream workflows.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/confirm-product/${ecomId}`)}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold shadow-sm"
            >
              Review & confirm details
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex gap-6 mt-6">
          {/* Left Sidebar - Tab Navigation (Desktop) */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-4 sticky top-4">
              <nav className="space-y-1">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  const progress = laneProgress[tab.lane];
                  const isActive = activeTab === tab.id;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`tab-${tab.id}`}
                      className={cn(
                        'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-left transition-all font-medium text-sm group',
                        isActive
                          ? 'bg-cyan-500 text-white shadow-md'
                          : 'text-slate-700 hover:bg-slate-100'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={cn('w-5 h-5', isActive ? 'text-white' : 'text-slate-500')} />
                    <span>{tab.label}</span>
                  </div>
                  <div className={cn(
                    'w-2 h-2 rounded-full',
                    progress >= 100 ? 'bg-emerald-400' :
                    progress > 0 ? 'bg-amber-400' : 'bg-slate-300'
                  )} />
                </button>
              );
            })}
          </nav>

          {/* Progress Summary */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3">
              Lane Progress
            </h4>
            <div className="space-y-2">
              <MiniProgressBar value={laneProgress.research} lane="research" label="Research" />
              <MiniProgressBar value={laneProgress.ugc} lane="ugc" label="UGC" />
              <MiniProgressBar value={laneProgress.winning} lane="winning" label="Winning" />
              <MiniProgressBar value={laneProgress.ads} lane="ads" label="Ads" />
              <MiniProgressBar value={laneProgress.shopify} lane="shopify" label="Shopify" />
            </div>
          </div>
          </div>
        </div>

          {/* Right Content Area */}
          <div className="flex-1 bg-white rounded-xl border border-slate-200 p-6">
            {activeTab === 'research' && (
              <ResearchTab product={product} data={relatedData} />
            )}
            {activeTab === 'ugc' && (
              <UGCTab data={relatedData} />
            )}
            {activeTab === 'winning' && (
              <WinningTab data={relatedData} />
            )}
            {activeTab === 'ads' && (
              <AdsTab data={relatedData} />
            )}
            {activeTab === 'shopify' && (
              <ShopifyTab data={relatedData} />
            )}
          </div>
        </div>

        {/* Mobile Bottom Tab Bar */}
        <div className="lg:hidden fixed bottom-0 left-64 right-0 bg-white border-t-2 border-slate-200 z-40 overflow-x-auto">
          <div className="flex">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex-1 flex flex-col items-center gap-1 py-3 px-4 min-w-[80px]',
                    isActive ? 'text-cyan-600 bg-cyan-50' : 'text-slate-500'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-xs font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}

// Tab Components
function ResearchTab({ product, data }) {
  const { reviews, marketing } = data;

  return (
    <div className="space-y-8" data-testid="research-tab">
      <div>
        <h2 className="text-2xl font-bold text-[#1E1B4B] mb-6 font-['Manrope']">Research & Analysis</h2>
        
        {/* Product Status */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Product Details</h3>
            <StatusBadge status={product?.product_status} lane="research" />
          </div>
          
          {product?.markdown && (
            <div className="prose prose-sm max-w-none text-slate-700">
              <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg overflow-auto max-h-64">
                {product.markdown}
              </pre>
            </div>
          )}
          
          {product?.additional_info && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-slate-600 mb-2">Additional Info</h4>
              <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg overflow-auto max-h-48">
                {product.additional_info}
              </pre>
            </div>
          )}
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Reviews Analysis</h3>
            <StatusBadge status={reviews?.review_status} lane="research" />
          </div>
          
          {reviews ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-emerald-600 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Positive Reviews
                </h4>
                <pre className="whitespace-pre-wrap text-sm bg-emerald-50 p-4 rounded-lg overflow-auto max-h-48 text-slate-700">
                  {reviews.positive_reviews || 'Not analyzed yet'}
                </pre>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Negative Reviews
                </h4>
                <pre className="whitespace-pre-wrap text-sm bg-red-50 p-4 rounded-lg overflow-auto max-h-48 text-slate-700">
                  {reviews.negative_reviews || 'Not analyzed yet'}
                </pre>
              </div>
            </div>
          ) : (
            <p className="text-slate-500">Reviews not analyzed yet</p>
          )}
        </div>

        {/* Marketing Angles */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Marketing Angles</h3>
            <StatusBadge status={marketing?.status} lane="research" />
          </div>
          
          {marketing ? (
            <div className="space-y-4">
              {marketing.marketing_angle && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">Marketing Angle</h4>
                  <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg overflow-auto max-h-32">
                    {marketing.marketing_angle}
                  </pre>
                </div>
              )}
              {marketing.competitive_edge && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">Competitive Edge</h4>
                  <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg overflow-auto max-h-32">
                    {marketing.competitive_edge}
                  </pre>
                </div>
              )}
              {marketing.customer_personas && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">Customer Personas</h4>
                  <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg overflow-auto max-h-32">
                    {marketing.customer_personas}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500">Marketing angles not generated yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function UGCTab({ data }) {
  const { ugc, ugcFullVideos, ugcShorts } = data;

  return (
    <div className="space-y-8" data-testid="ugc-tab">
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#1E1B4B] font-['Manrope']">UGC Videos</h2>
          <StatusBadge status={ugc?.status} lane="ugc" />
        </div>

        {/* Full Videos */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Full Videos</h3>
            <StatusBadge status={ugc?.full_video_status || ugcFullVideos?.status} lane="ugc" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <VideoCard url={ugcFullVideos?.video1} label="Video 1 Pending" />
            <VideoCard url={ugcFullVideos?.video2} label="Video 2 Pending" />
            <VideoCard url={ugcFullVideos?.video3} label="Video 3 Pending" />
            <VideoCard url={ugcFullVideos?.video4} label="Video 4 Pending" />
          </div>
        </div>

        {/* Shorts */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Short Videos</h3>
            <StatusBadge status={ugc?.shorts_status || ugcShorts?.status} lane="ugc" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <VideoCard url={ugcShorts?.video1} label="Short 1" />
            <VideoCard url={ugcShorts?.video2} label="Short 2" />
            <VideoCard url={ugcShorts?.video3} label="Short 3" />
            <VideoCard url={ugcShorts?.video4} label="Short 4" />
          </div>
        </div>
      </div>
    </div>
  );
}

function WinningTab({ data }) {
  const { winning, winningImages } = data;

  return (
    <div className="space-y-8" data-testid="winning-tab">
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#1E1B4B] font-['Manrope']">Winning Product</h2>
          <StatusBadge status={winning?.status} lane="winning" />
        </div>

        {/* Product Details */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-[#1E1B4B] mb-4">Product Copy</h3>
          
          {winning ? (
            <div className="space-y-4">
              {winning.product_name && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-1">Product Name</h4>
                  <p className="text-lg font-bold text-[#1E1B4B]">{winning.product_name}</p>
                </div>
              )}
              {winning.price && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-1">Price</h4>
                  <p className="text-2xl font-bold text-emerald-600">${winning.price}</p>
                </div>
              )}
              {winning.description && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">Description</h4>
                  <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg">
                    {winning.description}
                  </pre>
                </div>
              )}
              {winning.features && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">Features</h4>
                  <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg">
                    {winning.features}
                  </pre>
                </div>
              )}
              {winning.how_to_use && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-600 mb-2">How to Use</h4>
                  <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg">
                    {winning.how_to_use}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500">Winning product details not generated yet</p>
          )}
        </div>

        {/* Images */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Hero Images</h3>
            <StatusBadge status={winningImages?.status} lane="winning" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map(i => {
              const imageUrl = winningImages?.[`image${i}`];
              return (
                <div key={i} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                  {imageUrl ? (
                    <img src={imageUrl} alt={`Hero ${i}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdsTab({ data }) {
  const { staticAds } = data;

  return (
    <div className="space-y-8" data-testid="ads-tab">
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#1E1B4B] font-['Manrope']">Static Ads</h2>
          <StatusBadge status={staticAds?.status} lane="ads" />
        </div>

        {staticAds?.full_ad_strategy && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-[#1E1B4B] mb-4">Ad Strategy</h3>
            <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-4 rounded-lg overflow-auto max-h-64">
              {staticAds.full_ad_strategy}
            </pre>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <AdSetCard
              key={i}
              adNumber={i}
              images={staticAds?.[`ad${i}_images`]}
              prompts={staticAds?.[`ad${i}_prompts`]}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ShopifyTab({ data }) {
  const { shopify, shopifyImages, shopifySections } = data;

  return (
    <div className="space-y-8" data-testid="shopify-tab">
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#1E1B4B] font-['Manrope']">Shopify Integration</h2>
          <div className="flex items-center gap-3">
            {shopify?.product_page && (
              <a
                href={shopify.product_page}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                data-testid="view-on-shopify-btn"
              >
                <ShoppingBag className="w-4 h-4" />
                View on Shopify
              </a>
            )}
            <StatusBadge status={shopify?.status} lane="shopify" />
          </div>
        </div>

        {/* Product Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-[#1E1B4B] mb-4">Product Information</h3>
          
          {shopify ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {shopify.product_name && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-1">Product Name</h4>
                    <p className="font-medium text-[#1E1B4B]">{shopify.product_name}</p>
                  </div>
                )}
                {shopify.shopify_product_id && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-1">Shopify Product ID</h4>
                    <code className="text-sm bg-slate-100 px-2 py-1 rounded">{shopify.shopify_product_id}</code>
                  </div>
                )}
                {shopify.description && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-1">Description</h4>
                    <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-3 rounded-lg max-h-32 overflow-auto">
                      {shopify.description}
                    </pre>
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {shopify.features && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-1">Features</h4>
                    <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-3 rounded-lg max-h-32 overflow-auto">
                      {shopify.features}
                    </pre>
                  </div>
                )}
                {shopify.how_to_use && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-600 mb-1">How to Use</h4>
                    <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-3 rounded-lg max-h-32 overflow-auto">
                      {shopify.how_to_use}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-slate-500">Shopify product not created yet</p>
          )}
        </div>

        {/* Images */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Product Images</h3>
            <StatusBadge status={shopifyImages?.status || shopify?.image_gen_status} lane="shopify" />
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => {
              const imageUrl = shopifyImages?.[`image${i}`];
              return (
                <div key={i} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                  {imageUrl ? (
                    <img src={imageUrl} alt={`Product ${i}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sections */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1E1B4B]">Page Sections</h3>
            <StatusBadge status={shopifySections?.status} lane="shopify" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <SectionCard title="FAQ Section" content={shopifySections?.faq_section} />
            <SectionCard title="Testimonials" content={shopifySections?.testimonials} />
            <SectionCard title="Accordion" content={shopifySections?.accordion} />
            <SectionCard title="Compare Section" content={shopifySections?.compare_section} />
            <SectionCard title="Benefits Section" content={shopifySections?.benefits_section} />
            <SectionCard title="Claims & Reviews" content={shopifySections?.claims_reviews} />
          </div>
        </div>
      </div>
    </div>
  );
}
